import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { SetorService } from '../services/setor.service';
import { UnidadeService } from '../services/unidade.service';
import { UsuarioService, UsuarioPayload, ApiResponse } from '../services/usuario.service';
import { JornadaService} from '../services/jornada.service';
import { FuncaoService } from '../services/funcao.service';
import { ImageCropperComponent, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import { parseBackendResponse } from '../services/api-response.util';
import { HeaderComponent } from '../shared/header/header.component';

interface DropdownItem { id: number; nome: string; }

@Component({
  selector: 'app-usuario',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, HttpClientModule, ImageCropperComponent, HeaderComponent],
  templateUrl: './usuario.component.html',
  styleUrls: ['./usuario.component.scss']
})
export class UsuarioComponent implements OnInit {
  @ViewChild(ImageCropperComponent) imageCropper?: ImageCropperComponent;

  usuarioForm!: FormGroup;
  photoPreview: string | null = null;
  photoFileName: string | null = null;
  photoMimeType: string | null = null;
  imageChangedEvent: Event | null = null;
  imageBase64?: string;
  croppedImage: string | null = null;
  isCropperOpen = false;
  cropperScale = 1;
  transform: ImageTransform = {};

  tiposDocumento = [
    { id: 1, nome: 'CPF' },
    { id: 2, nome: 'CNPJ' }
  ];

  setores: DropdownItem[] = [];
  unidades: DropdownItem[] = [];
  jornadas: DropdownItem[] = [];
  funcoes: DropdownItem[] = [];
  mostrarSenha: boolean = false;

  constructor(
    private fb: FormBuilder,
    private setorService: SetorService,
    private unidadeService: UnidadeService,
    private jornadaService: JornadaService,
    private usuarioService: UsuarioService,
    private funcaoService: FuncaoService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {

    this.usuarioForm = this.fb.group({
      dadosBasicos: this.fb.group({
        nome: ['', [Validators.required, Validators.minLength(3)]],
        cpfCnpj: ['', Validators.required],
        idTipoUsuario: [null, [Validators.required, Validators.min(1)]] 
      }),
      autenticacao: this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        senha: ['', [Validators.required, Validators.minLength(6)]]
      }),
      contato: this.fb.group({
        telefoneCelular: ['', Validators.required],
        contatoEmergencia: ['', Validators.required],
        telefoneEmergencia: ['', Validators.required]
      }),
      endereco: this.fb.group({
        cep: ['', Validators.required],
        logradouro: [''], numero: [''], cidade: [''],
        bairro: [''], estado: [''], complemento: ['']
      }),
      dadosPessoais: this.fb.group({
        fotoBase64: [''],
        fotoMimeType: [''],
        salarioBase: [null, [Validators.required, Validators.min(0.01)]],
        dataAdmissao: ['', Validators.required],
        genero: [''],
        idSetor: [null, Validators.required],
        idUnidade: [null, Validators.required],
        idJornadaTrabalho: [null, Validators.required],
        idFuncao: [null, Validators.required]
      }),
    });

    this.carregarDropdowns();
  }

  toggleSenha(): void {
    this.mostrarSenha = !this.mostrarSenha;
  }

  carregarDropdowns(): void {
    this.carregarSetores();
    this.carregarUnidades();
    this.carregarJornadas();
    this.carregarFuncoes();
  }

  carregarSetores() {
    this.setorService.getSetores().subscribe({
      next: (dados: any[]) => {
        if (dados) {          
          this.setores = dados.map(item => ({
            id: item.idSetor,
            nome: item.descricao
          }));
        }
      },
      error: (err: any) => {
        console.error('Erro ao carregar SETORES:', err);
        this.toastr.error('Não foi possível carregar os setores.', 'Erro de API');
      }
    });
  }

  carregarUnidades() {
    this.unidadeService.getUnidades().subscribe({
      next: (dados: any[]) => {
        if (dados) {
          this.unidades = dados.map(item => ({
            id: item.idUnidade,
            nome: item.descricao
          }));
        }
      },
      error: (err: any) => {
        console.error('Erro ao carregar UNIDADES:', err);
        this.toastr.error('Não foi possível carregar as unidades.', 'Erro de API');
      }
    });
  }

  carregarJornadas() {
    this.jornadaService.getJornadas().subscribe({
      next: (dados: any[]) => {
        if (dados) {
          this.jornadas = dados.map(item => ({
            id: item.idJornadaTrabalho || item.id, 
            nome: item.nomeJornada
          }));
        }
      },
      error: (err:any) => {
        console.error('Erro ao carregar JORNADAS:', err);
        this.toastr.error('Não foi possível carregar as jornadas de trabalho.', 'Erro de API');
      }
    });
  }

  carregarFuncoes() {
    this.funcaoService.getFuncoes().subscribe({
      next: (dados: any[]) => {
        this.funcoes = dados.map(item => ({
          id: item.id,
          nome: item.nome
        }));
      },
      error: (err) => {
        console.error('Erro ao carregar FUNÇÕES:', err);
        this.toastr.error('Não foi possível carregar as funções.', 'Erro de API');
       }
    });
  }

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.photoFileName = file.name;
    this.photoMimeType = file.type || null;
    this.imageChangedEvent = event;
    this.imageBase64 = undefined;
    this.isCropperOpen = true;
    this.cropperScale = 1;
    this.transform = {};
    this.croppedImage = null;

    // Console log para debug da imagem selecionada
    console.log('📸 IMAGEM SELECIONADA:', {
      nomeArquivo: file.name,
      tipoMime: file.type,
      tamanhoBytes: file.size,
      tamanhoKB: (file.size / 1024).toFixed(2) + ' KB',
      tamanhoMB: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
    });
  }

  removePhoto(): void {
    this.usuarioForm.get('dadosPessoais.fotoBase64')?.setValue('');
    this.usuarioForm.get('dadosPessoais.fotoMimeType')?.setValue('');
    this.photoPreview = null;
    this.photoFileName = null;
    this.photoMimeType = null;
    this.imageChangedEvent = null;
    this.imageBase64 = undefined;
    this.croppedImage = null;
    this.transform = {};
    this.cropperScale = 1;
  }

  openCropperFromPreview(): void {
    if (!this.photoPreview) {
      return;
    }
    this.imageChangedEvent = null;
    this.imageBase64 = this.photoPreview;
    this.isCropperOpen = true;
    this.cropperScale = 1;
    this.transform = {};
    this.croppedImage = null;
  }

  onImageCropped(event: ImageCroppedEvent): void {
    this.croppedImage = event.base64 ?? null;
  }

  onZoomChange(value: string | number): void {
    const numericValue = typeof value === 'string' ? Number(value) : value;
    this.cropperScale = numericValue;
    this.transform = {
      ...this.transform,
      scale: numericValue
    };
  }

  onZoomInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) {
      return;
    }
    this.onZoomChange(target.value);
  }

  confirmCrop(): void {
    let finalImage = this.croppedImage;
    if (!finalImage) {
      const manualCrop = this.imageCropper?.crop('base64');
      finalImage = manualCrop?.base64 ?? null;
    }
    if (!finalImage) {
      return;
    }
    const [metadata, base64Data] = finalImage.split(',');
    const mimeMatch = metadata?.match(/data:(.*?);base64/);
    const mimeType = mimeMatch ? mimeMatch[1] : this.photoMimeType ?? 'image/png';

    // Console log detalhado para debug da imagem processada
    const base64Final = base64Data ?? finalImage;
    console.log('✂️ IMAGEM PROCESSADA (Após Crop):', {
      metadata: metadata || 'Não encontrado',
      mimeTypeExtraido: mimeType,
      base64CompletoLength: finalImage.length,
      base64SemMetadataLength: base64Final.length,
      base64Inicio: base64Final.length > 50 ? base64Final.substring(0, 50) + '...' : base64Final,
      base64Fim: base64Final.length > 50 ? '...' + base64Final.substring(base64Final.length - 50) : '',
      tamanhoAproximadoKB: (base64Final.length * 0.75 / 1024).toFixed(2) + ' KB',
      base64Completo: finalImage.length > 100 ? finalImage.substring(0, 100) + '... [truncado]' : finalImage
    });

    this.photoPreview = finalImage;
    this.photoMimeType = mimeType;
    this.usuarioForm.get('dadosPessoais.fotoBase64')?.setValue(base64Final);
    this.usuarioForm.get('dadosPessoais.fotoMimeType')?.setValue(mimeType);
    
    // Verificar o que foi salvo no formulário
    const fotoBase64Salva = this.usuarioForm.get('dadosPessoais.fotoBase64')?.value;
    const fotoMimeTypeSalva = this.usuarioForm.get('dadosPessoais.fotoMimeType')?.value;
    console.log('💾 DADOS SALVOS NO FORMULÁRIO:', {
      fotoBase64Length: fotoBase64Salva?.length || 0,
      fotoBase64Inicio: fotoBase64Salva && fotoBase64Salva.length > 50 
        ? fotoBase64Salva.substring(0, 50) + '...' 
        : fotoBase64Salva || 'vazio',
      fotoMimeType: fotoMimeTypeSalva || 'não definido'
    });
    
    this.closeCropper();
  }

  closeCropper(): void {
    this.isCropperOpen = false;
    this.imageChangedEvent = null;
    this.imageBase64 = undefined;
    this.transform = {};
    this.cropperScale = 1;
  }

  buscarCep(): void {
  const enderecoGroup = this.usuarioForm.get('endereco');
  const cep = enderecoGroup?.get('cep')?.value;

  if (!cep) return;

  const cepLimpo = cep.replace(/\D/g, '');

  if (cepLimpo.length === 8) {
    this.usuarioService.buscarCep(cepLimpo).subscribe({
      next: (dados) => {
        enderecoGroup?.patchValue({
          logradouro: dados.logradouro,
          bairro: dados.bairro,
          cidade: dados.localidade,
          estado: dados.uf
        });
      },
      error: (erro) => {
        console.error('Erro ao buscar CEP:', erro);
      }
    });
  }
}

  onSubmit() {
    if (this.usuarioForm.invalid) {
      this.usuarioForm.markAllAsTouched();
      this.toastr.warning('Por favor, preencha todos os campos obrigatórios.', 'Formulário Inválido');
      return;
    }

    const formValues = this.usuarioForm.getRawValue();
    formValues.dadosBasicos.idTipoUsuario = Number(formValues.dadosBasicos.idTipoUsuario);
    

    const selectedFuncao = this.funcoes.find(f => f.id === formValues.dadosPessoais.idFuncao);
    const dadosPessoaisComCargo = {
      ...formValues.dadosPessoais,
      cargo: selectedFuncao ? selectedFuncao.nome : ''
    };

     const dadosBasicosComTipoCorrigido = {
    ...formValues.dadosBasicos,
    tipoUsuarioID: formValues.dadosBasicos.idTipoUsuario
  };
  delete dadosBasicosComTipoCorrigido.idTipoUsuario;

    const payload: UsuarioPayload = {
  dadosBasicos: dadosBasicosComTipoCorrigido, 
  autenticacao: formValues.autenticacao,
  contato: formValues.contato,
  endereco: formValues.endereco,
  dadosPessoais: dadosPessoaisComCargo,
  usuarioResponsavelPeloCadastro: 'Admin Sistema'
};

    
    console.log('📤 PAYLOAD FINAL - DADOS DA FOTO:', {
      temFoto: !!payload.dadosPessoais.fotoBase64,
      fotoBase64Length: payload.dadosPessoais.fotoBase64?.length || 0,
      fotoBase64Inicio: payload.dadosPessoais.fotoBase64 && payload.dadosPessoais.fotoBase64.length > 100
        ? payload.dadosPessoais.fotoBase64.substring(0, 100) + '...'
        : payload.dadosPessoais.fotoBase64 || 'vazio',
      fotoBase64Fim: payload.dadosPessoais.fotoBase64 && payload.dadosPessoais.fotoBase64.length > 50
        ? '...' + payload.dadosPessoais.fotoBase64.substring(payload.dadosPessoais.fotoBase64.length - 50)
        : 'vazio',
      fotoMimeType: payload.dadosPessoais.fotoMimeType || 'não definido',
      tamanhoAproximadoBytes: payload.dadosPessoais.fotoBase64 
        ? Math.round(payload.dadosPessoais.fotoBase64.length * 0.75)
        : 0,
      tamanhoAproximadoKB: payload.dadosPessoais.fotoBase64
        ? (payload.dadosPessoais.fotoBase64.length * 0.75 / 1024).toFixed(2) + ' KB'
        : '0 KB',
      fotoBase64Completo: payload.dadosPessoais.fotoBase64 || 'não enviado'
    });

    console.log('📦 PAYLOAD FINAL COMPLETO:', payload); 

    this.usuarioService.cadastrarUsuario(payload).subscribe({
      next: (response) => {
        const parsed = parseBackendResponse(response, 201);
        const mensagem = parsed.mensagem || (parsed.mensagens && parsed.mensagens.join('\n')) || 'Usuário cadastrado com sucesso!';
        this.toastr.success(mensagem, 'Sucesso!');
        this.usuarioForm.reset();
      },
      error: (err) => {
        
        console.error('Erro detalhado da API:', err);
        const parsed = parseBackendResponse(err?.error, err?.status);
        let errorMsg = parsed.mensagem || (parsed.mensagens && parsed.mensagens.join('\n')) || 'Ocorreu um erro desconhecido.';

        this.toastr.error(errorMsg, 'Erro ao Cadastrar');
      }
    });
  }
}

